
import { Options, mixins } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { constant } from '@/utils/constants'
import { IAudience, IAudienceOrNot, IPushAudience, IRichMenuAudience } from '@/utils/types'
import { ACTION_AUDIENCE } from '@/store/actions'
import PushCampaignMixin from '@/components/push-campaign/mixins/PushCampaignMixin.vue'
import draggable from 'vuedraggable'
import AudieneceDndList from './AudieneceDndList.vue'

@Options({
  components: { draggable, AudieneceDndList },
  directives: { maska },
  emits: ['update:modelValue', 'update:handleLoading'],
})
export default class AudienceOrNotSetting extends mixins(PushCampaignMixin) {
  @Prop({})
  modelValue: IPushAudience | IRichMenuAudience = {
    audience_or: [],
    audience_not: [],
  }

  @Prop({})
  isTestMode!: boolean

  @Prop({ default: true })
  showToolbar!: boolean

  totalAudienceProd = 0
  allProd!: IAudience
  audienceOr: IAudienceOrNot[] = []
  audienceNot: IAudienceOrNot[] = []
  audiences: IAudience[] = []

  get value() {
    return this.modelValue
  }

  set value(value: IPushAudience | IRichMenuAudience) {
    this.$emit('update:modelValue', value)
  }

  async getAudienceOr() {
    this.audienceOr = []
    if (this.value.audience_or) {
      for (let index = 0; index < this.value.audience_or.length; index++) {
        const element = this.value.audience_or[index]
        if (element._id === constant.DEFAULT_ALL_USER_AUDIENCE_ID) {
          if (this.allProd) {
            this.audienceOr.push({
              _id: this.allProd._id,
              title: this.allProd.title,
              audience_size: this.allProd.audience_size,
            })
          }
        } else {
          let result = this.audiences.find((or) => or._id === element._id)
          if (!result) {
            result = await this.$store.dispatch(ACTION_AUDIENCE.LOAD_ITEM, element._id)
          }

          if (result) {
            this.audienceOr.push({
              _id: element._id,
              title: result.title,
              audience_size: result.audience_size,
            })
          }
        }
      }
    }
  }

  async getAudienceNot() {
    this.audienceNot = []
    if (this.value.audience_not) {
      for (let index = 0; index < this.value.audience_not.length; index++) {
        const element = this.value.audience_not[index]
        if (element._id === constant.DEFAULT_ALL_USER_AUDIENCE_ID) {
          if (this.allProd) {
            this.audienceNot.push({
              _id: this.allProd._id,
              title: this.allProd.title,
              audience_size: this.allProd.audience_size,
            })
          }
        } else {
          let result = this.audiences.find((not) => not._id === element._id)
          if (!result) {
            result = await this.$store.dispatch(ACTION_AUDIENCE.LOAD_ITEM, element._id)
          }

          if (result) {
            this.audienceNot.push({
              _id: element._id,
              title: result.title,
              audience_size: result.audience_size,
            })
          }
        }
      }
    }
  }

  @Watch('selectedAppId', { immediate: true })
  async updateApi() {
    if (!this.selectedAppId) {
      return
    }

    this.$emit('update:handleLoading', true)
    this.audiences = []
    const result = await this.$store.dispatch(ACTION_AUDIENCE.LOAD_USER_ITEMS, {
      app_id: this.selectedAppId,
      is_test_mode: this.isTestMode,
    })
    // eslint-disable-next-line
    if (result && result['count_active_users'] > 0) {
      this.allProd = {
        _id: constant.DEFAULT_ALL_USER_AUDIENCE_ID,
        app_id: this.selectedAppId,
        title: this.$t('label.all_user_audience_id'),
        // eslint-disable-next-line
        audience_size: result['count_active_users'],
        filter_condition: {
          answers: {},
          date_range: {
            from: '',
            to: '',
          },
        },
      }
      // eslint-disable-next-line
      this.totalAudienceProd = result['total_audiences']
    }

    await this.getAudienceOr()
    await this.getAudienceNot()
    await this.getAudienceSource()
    return result
  }

  async onChangeAudienceOr(audienceOr: IAudienceOrNot[]) {
    this.value.audience_or = audienceOr
    await this.getAudienceOr()
  }

  async onChangeAudienceNot(audienceNot: IAudienceOrNot[]) {
    this.value.audience_not = audienceNot
    await this.getAudienceNot()
  }

  async getAudienceSource(filter?: unknown) {
    this.audiences = []
    const pagination = {
      page: 1,
      rowsPerPage: constant.ROW_PER_PAGE,
      sortBy: '',
      descending: false,
    }

    this.$emit('update:handleLoading', true)
    const fetchCount = pagination.rowsPerPage === 0 ? this.totalAudienceProd : pagination.rowsPerPage
    const startRow = (pagination.page - 1) * pagination.rowsPerPage
    const returnedData: [] = await this.fetchFromServer(
      startRow,
      fetchCount,
      filter,
      pagination.sortBy,
      pagination.descending,
      this.isTestMode
    )

    if (pagination.page === 1) {
      if (this.allProd && this.allProd.audience_size && this.allProd.audience_size > 0) {
        this.audiences.push(this.allProd)
      }

      this.audiences = this.audiences.concat(returnedData)
    } else {
      this.audiences.splice(0, this.audiences.length, ...returnedData)
    }

    this.audiences = this.audiences.filter((audience) => {
      const isExistOr = this.audienceOr.find((or) => or._id === audience._id)
      const isExistNot = this.audienceNot.find((not) => not._id === audience._id)
      return !isExistOr && !isExistNot
    })

    this.$emit('update:handleLoading', false)
  }

  async fetchFromServer(startRow, count, filter, sortBy, descending, isTestMode) {
    const items = await this.$store.dispatch(ACTION_AUDIENCE.LOAD_SIMPLE_ITEMS, {
      app_id: this.selectedAppId,
      is_test_mode: isTestMode,
      is_include_data: false,
      filter: {
        start_row: startRow,
        rows_number: count,
        filter: filter,
        sort_by: sortBy,
        descending: descending,
      },
    })
    return items
  }
}
